import { useState, type FormEvent } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Send } from 'lucide-react';
import type { CreateSupportTicketResponse } from '@lira/shared';
import { Modal } from '@/components/ui';
import { apiFormPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';

const MIN_DETAILS = 40;

/** Fluxo curto: descrição objetiva → revisão/confirmação. */
export function SimpleSupportReporter({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<CreateSupportTicketResponse | null>(null);
  const canReview = title.trim().length >= 3 && details.trim().length >= MIN_DETAILS;

  const resetAndClose = () => {
    setStep(1);
    setTitle('');
    setDetails('');
    setSubmitted(null);
    onClose();
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canReview || submitting) return;
    setSubmitting(true);
    try {
      const narrative = `Título: ${title.trim()}\n\n${details.trim()}`;
      const body = new FormData();
      body.set('kind', 'bug');
      body.set('pagePath', window.location.pathname);
      body.set('pageLabel', document.title || 'Tela atual');
      body.set('happenedDetails', narrative);
      body.set('reproductionDetails', details.trim());
      const result = await apiFormPost<CreateSupportTicketResponse>('/support/tickets', body);
      setSubmitted(result);
      toast('Reporte enviado. Obrigado por ajudar a melhorar o Lira.', 'success');
    } catch (error) {
      toast(errorMessage(error), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? () => undefined : resetAndClose} title="Reportar problema" maxWidth={560}>
      {submitted ? (
        <div className="py-5 text-center">
          <CheckCircle2 className="mx-auto size-12 text-income" />
          <h3 className="mt-3 text-lg font-semibold text-ink">Reporte enviado</h3>
          <p className="mt-1 text-sm text-muted">Código: <span className="font-mono text-ink">{submitted.id}</span></p>
          <button type="button" onClick={resetAndClose} className="mt-6 h-10 rounded-full bg-primary px-5 text-sm font-semibold uppercase tracking-wide text-white">Concluir</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <ol className="mb-5 grid grid-cols-2 gap-2 text-center text-xs font-semibold">
            {['Relato', 'Revisão'].map((label, index) => <li key={label} className={step >= index + 1 ? 'rounded-full bg-primary-weak py-2 text-primary' : 'rounded-full bg-canvas py-2 text-muted'}>{index + 1}. {label}</li>)}
          </ol>

          {step === 1 ? (
            <div className="space-y-4">
              <div><label htmlFor="support-title" className="text-sm font-semibold text-ink">Título</label><input id="support-title" value={title} onChange={(event) => setTitle(event.target.value)} minLength={3} required placeholder="Ex.: saldo da conta está incorreto" className="mt-2 h-11 w-full rounded-lg border border-field bg-surface px-3 text-sm text-ink outline-none focus:border-primary" /></div>
              <div><label htmlFor="support-details" className="text-sm font-semibold text-ink">O que aconteceu?</label><textarea id="support-details" value={details} onChange={(event) => setDetails(event.target.value)} minLength={MIN_DETAILS} required rows={7} placeholder="Conte o que esperava, o que ocorreu e em qual tela." className="mt-2 w-full resize-y rounded-lg border border-field bg-surface p-3 text-sm text-ink outline-none focus:border-primary" /><p className="mt-1 text-xs text-muted">{Math.max(0, MIN_DETAILS - details.trim().length) || 'Detalhe suficiente'}{details.trim().length < MIN_DETAILS ? ' caracteres restantes.' : '.'}</p></div>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-border bg-canvas p-4"><p className="text-sm text-muted">Revise antes de enviar. Nenhuma captura de tela será anexada automaticamente.</p><div><p className="text-xs font-semibold uppercase tracking-wide text-muted">Título</p><p className="mt-1 font-medium text-ink">{title}</p></div><div><p className="text-xs font-semibold uppercase tracking-wide text-muted">Relato</p><p className="mt-1 whitespace-pre-wrap text-sm text-ink">{details}</p></div></div>
          )}

          <div className="mt-6 flex justify-between gap-3">
            <button type="button" onClick={() => step === 1 ? resetAndClose() : setStep(1)} disabled={submitting} className="flex h-10 items-center gap-1 rounded-full px-4 text-sm font-semibold text-muted hover:bg-canvas hover:text-ink"><ChevronLeft className="size-4" />{step === 1 ? 'Cancelar' : 'Voltar'}</button>
            {step === 1 ? <button type="button" onClick={() => canReview && setStep(2)} disabled={!canReview} className="flex h-10 items-center gap-1 rounded-full bg-primary px-5 text-sm font-semibold text-white disabled:opacity-50">Revisar<ChevronRight className="size-4" /></button> : <button type="submit" disabled={submitting} className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-white disabled:opacity-60">{submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}Confirmar e enviar</button>}
          </div>
        </form>
      )}
    </Modal>
  );
}
